import {
  Input,
  Output,
  OnInit,
  ViewChild,
  OnDestroy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { map } from 'rxjs/operators';
import { Observable, Subject, Subscription } from 'rxjs';

import { AuthApiService, DocumentOwnerType, FileContentResultInterface, FileStorageService, HttpStatusCodesEnum } from '@spa/api';

import { Helper } from '../../classes';
import { FileMimeTypesEnum } from '../../enums';

@Component({
  selector: 'app-upload-document',
  templateUrl: Helper.getPortalTemplate(),
  styleUrls: [Helper.getPortalStyle()],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UploadDocumentComponent implements OnInit, OnDestroy {
  private documentDataUrl: string;
  private documentSubjectSubscription: Subscription | null;

  public file: File | null;
  public documentUrl: SafeResourceUrl;
  public getFileTypeByName: (fileName: string) => string;
  public pdfFileType: string;
  public imageFileTypes: string[];
  public hasGetDocumentError: boolean;
  public hiddenPreloader: boolean;

  @Input() public hint: string;
  @Input() public readonly: boolean;
  @Input() public documentSubject: Subject<string | null> | undefined;
  @Input() public documentErrorSubject: Subject<string[]> | undefined;

  @Output() public previewDocument: EventEmitter<string>;
  @Output() public documentUploaded: EventEmitter<File | null>;

  @ViewChild('fileUploadInput') public fileUploadInput: ElementRef;

  constructor(
    private fileStorageService: FileStorageService,
    private authService: AuthApiService,
    private sanitizer: DomSanitizer,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.documentSubjectSubscription = null;

    this.file = null;
    this.documentUrl = '';
    this.documentDataUrl = '';
    this.getFileTypeByName = Helper.filesHelper().getFileMimeType;
    this.pdfFileType = FileMimeTypesEnum.Pdf;
    this.imageFileTypes = Helper.filesHelper().getImageFileMimeTypes();
    this.hasGetDocumentError = false;
    this.hiddenPreloader = false;

    this.readonly = false;

    this.previewDocument = new EventEmitter();
    this.documentUploaded = new EventEmitter();

    this.fileUploadInput = {} as ElementRef;
  }

  private getDocumentOwnerTypeAndUserIdOrCompanyId(): { documentOwnerType: DocumentOwnerType; userId: number; } {
    const userId = this.authService.getAuthenticatedUser().id;

    return {
      documentOwnerType: DocumentOwnerType.OwnerIsIndividual,
      userId
    };
  }

  ngOnInit(): void {
    if (this.documentSubject) {
      this.documentSubjectSubscription = this.documentSubject.subscribe(() => {
        this.file = null;
        this.documentUrl = '';
        this.hiddenPreloader = true;

        if (this.fileUploadInput) {
          this.fileUploadInput.nativeElement.value = null;
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.documentSubjectSubscription) {
      this.documentSubjectSubscription.unsubscribe();
    }
  }

  @HostListener('drop', ['$event'])
  onDocumentChange(event: Event): boolean {
    const eventTarget = event.target as HTMLInputElement;

    if (eventTarget.files && eventTarget.files.length > 0) {
      this.file = eventTarget.files[0];

      const fileReader = new FileReader();

      fileReader.readAsDataURL(this.file);

      fileReader.onload = () => {
        this.documentDataUrl = fileReader.result as string;
        this.documentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(fileReader.result as string);
        this.changeDetectorRef.detectChanges();

      };

      this.documentErrorSubject?.next([]);
      this.documentUploaded.emit(this.file);
    }

    return true;
  }

  initNonImageDocument(fileStorageId: string): Observable<boolean> {
    const { documentOwnerType, userId } = this.getDocumentOwnerTypeAndUserIdOrCompanyId();

    return this.fileStorageService.getFile(documentOwnerType, userId, fileStorageId)
      .pipe(map(response => {
        if (response.status === HttpStatusCodesEnum.Ok && response.data !== null) {
          const fileType = Helper.filesHelper().getFileMimeType(fileStorageId);
          const blob = Helper.filesHelper().convertBase64ToBlob((response.data as FileContentResultInterface).file_content, fileType);
          const blobItem = new Blob([blob], { type: fileType });
          const file = Helper.filesHelper().convertBlobToFile(blobItem, fileStorageId);

          this.documentUploaded.emit(file);
        }
        else {
          this.hasGetDocumentError = true;
        }

        return true;
      }));
  }

  getDocument(fileStorageId: string): Observable<SafeResourceUrl | string> {
    const { documentOwnerType, userId } = this.getDocumentOwnerTypeAndUserIdOrCompanyId();

    return this.fileStorageService
      .getFile(documentOwnerType, userId, fileStorageId)
      .pipe(map(response => {
        if (
          (response.status !== HttpStatusCodesEnum.Ok) ||
          (response.status === HttpStatusCodesEnum.Ok && response.data === null)
        ) {
          this.hasGetDocumentError = true;
          this.hiddenPreloader = true;
          this.changeDetectorRef.detectChanges();

          return this.sanitizer.bypassSecurityTrustResourceUrl('');
        }

        const fileType = Helper.filesHelper().getFileMimeType(fileStorageId);
        const fileContent = (response.data as FileContentResultInterface).file_content;
        const blob = Helper.filesHelper().convertBase64ToBlob(fileContent, fileType);
        const blobItem = new Blob([blob], { type: fileType });
        const file = Helper.filesHelper().convertBlobToFile(blobItem, fileStorageId);

        this.documentUploaded.emit(file);
        this.documentDataUrl = Helper.filesHelper().createBase64DataUrl(fileContent, fileType);
        const base64DataUrl = Helper.filesHelper().createBase64DataUrl(fileContent, fileType);

        return this.sanitizer.bypassSecurityTrustResourceUrl(base64DataUrl);
      }));
  }

  onPreviewDocument(): void {
    if (this.readonly && !this.hasGetDocumentError) {
      this.previewDocument.emit(this.documentDataUrl);
    }
  }

  removeDocument(): void {
    this.file = null;
    this.documentUrl = '';

    this.documentUploaded.emit(null);

    this.documentSubject?.next(null);
    this.documentErrorSubject?.next([]);

    if (this.fileUploadInput) {
      this.fileUploadInput.nativeElement.value = null;
    }
  }
}
